"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ArrowRight, Sparkles, ShieldCheck, MessageSquare, Compass, ChartBar as BarChart3, Cpu } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
interface TierPlan {
  id: string;
  name: string;
  badge: string;
  isPopular: boolean;
  priceRange: string;
  period: string;
  targetAudience: string;
  deliverables: string[];
  scopeHighlight: string;
}
const investmentPlans: TierPlan[] = [{
  id: "local-foundation",
  name: "Local SEO Foundation",
  badge: "Regional & Local Reach",
  isPopular: false,
  priceRange: "AED 2,000 – 4,000",
  period: "per month",
  targetAudience: "Ideal for single-location businesses, clinics, and local services targeting specific UAE catchments.",
  deliverables: ["Full Google Business Profile setup & optimization", "Localized keyword research & geo-targeted landing pages", "Essential on-page & metadata optimization", "Local citation building & NAP consistency audit", "Direct WhatsApp & email communication with me", "Monthly transparent performance & ranking report"],
  scopeHighlight: "Direct execution focused on capturing high-intent local footfall and service inquiries."
}, {
  id: "competitive-strategy",
  name: "Competitive SEO Strategy",
  badge: "Most Selected",
  isPopular: true,
  priceRange: "AED 4,000 – 7,000",
  period: "per month",
  targetAudience: "Tailored for growing B2B brands, established service providers, and multi-location firms in competitive niches.",
  deliverables: ["In-depth technical SEO architecture & Core Web Vitals fixes", "Comprehensive topical authority & content strategy", "High-intent commercial keyword mapping & optimization", "Quality digital PR & targeted editorial outreach", "AI Search & LLM citation readiness (ChatGPT, Perplexity)", "Bi-weekly strategy check-ins directly with Yassine", "Custom Looker Studio live analytics dashboard"],
  scopeHighlight: "Aggressive organic growth program built to outrank established industry competitors."
}, {
  id: "enterprise-program",
  name: "Enterprise SEO Program",
  badge: "Scale & Complex Portals",
  isPopular: false,
  priceRange: "AED 7,000 – 10,000+",
  period: "per month",
  targetAudience: "Designed for high-SKU ecommerce stores, multinational operations, and high-volume digital platforms.",
  deliverables: ["Advanced enterprise crawl budget & JavaScript rendering fixes", "International / multi-regional hreflang architecture", "Full-funnel organic content engine & conversion audits", "Dedicated digital asset link acquisition campaigns", "Algorithmic penalty recovery & ongoing risk mitigation", "Direct priority Slack/WhatsApp advisory channel", "Weekly strategic calls & bespoke executive analytics"],
  scopeHighlight: "Complete custom search strategy with hands-on senior consultant execution across every pillar."
}];
export default function Investments() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="seoinvestmentoptions" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wider uppercase border border-border">
            Transparent Retainer Engagements
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold tracking-tight text-foreground">
            SEO Investment Options
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            As an independent freelance SEO consultant, I partner directly with you. 
            Standard Dubai and UAE monthly retainers typically range between{" "}
            <span className="font-semibold text-foreground">AED 2,000 to AED 10,000+/month</span>, depending on niche competitiveness, technical debt, and search scale.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs sm:text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-primary" /> No Agency Middlemen
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Compass className="size-4 text-primary" /> 100% Customized Deliverables
            </span>
            <span className="inline-flex items-center gap-1.5">
              <BarChart3 className="size-4 text-primary" /> Transparent Reporting
            </span>
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {investmentPlans.map((plan, i) => <motion.div key={plan.id} data-index={i} variants={itemVariants} className="flex">
              <Card className={`relative flex flex-col justify-between w-full border rounded-2xl transition-[border-color,box-shadow] duration-300 ${plan.isPopular ? "border-primary shadow-xl bg-card ring-1 ring-primary/20" : "border-border shadow-sm hover:border-primary/40 hover:shadow-md bg-card"}`}>
                {plan.isPopular && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <Badge variant="default" className="gap-1 px-3 py-1 bg-primary text-primary-foreground font-semibold shadow-sm">
                      <Sparkles className="size-3.5" />
                      {plan.badge}
                    </Badge>
                  </div>}

                <div>
                  <CardHeader className="pb-4 pt-6">
                    {!plan.isPopular && <div className="mb-2">
                        <Badge variant="outline" className="text-muted-foreground border-border text-2xs uppercase tracking-wider font-medium">
                          {plan.badge}
                        </Badge>
                      </div>}
                    <CardTitle className="text-xl sm:text-2xl font-default font-bold text-foreground">
                      {plan.name}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm mt-2 text-muted-foreground min-h-[40px]">
                      {plan.targetAudience}
                    </CardDescription>
                  </CardHeader>

                  <div className="px-6 py-4 bg-muted/40 border-y border-border/60">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl sm:text-3xl font-bold font-default text-foreground tracking-tight">
                        {plan.priceRange}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground block mt-0.5 font-medium">
                      {plan.period} • No hidden lock-in contracts
                    </span>
                  </div>

                  <CardContent className="pt-6 pb-6">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                      Key Scope & Deliverables
                    </div>
                    <ul className="space-y-3">
                      {plan.deliverables.map((item, dIdx) => <li key={dIdx} className="flex items-start gap-2.5 text-sm text-foreground/90">
                          <span className="mt-0.5 rounded-full p-0.5 bg-primary/10 text-primary shrink-0">
                            <Check className="size-3.5 stroke-[2.5]" />
                          </span>
                          <span className="leading-snug">{item}</span>
                        </li>)}
                    </ul>

                    <Separator className="my-5" />

                    <div className="rounded-lg bg-background/80 p-3 border border-border/80 text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground font-medium block mb-0.5">Focus Outcome:</strong>
                      {plan.scopeHighlight}
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 pb-6">
                  <Button asChild variant={plan.isPopular ? "default" : "outline"} className="w-full h-11 text-sm font-medium transition-[background-color,border-color,box-shadow]">
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <span>Discuss This Option</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Guidance Note & Bottom CTA Section */}
        <div className="mt-16 sm:mt-20 rounded-2xl bg-muted/30 border border-border p-6 sm:p-10 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary mb-4">
            <MessageSquare className="size-6" />
          </div>
          <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground">
            Need a Bespoke SEO Scope or One-Off Technical Audit?
          </h3>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Every website faces unique structural challenges and organic opportunities. During our initial discovery call, I will review your current search footprint and propose an accurate, value-based roadmap tailored directly to your revenue goals.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Schedule Your SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-6 border-border hover:bg-card">
              <Link to="/CaseStudiesPage">
                Explore Verified Case Studies
              </Link>
            </Button>
          </div>

          <div className="mt-6 text-xs text-muted-foreground flex items-center justify-center gap-6">
            <span className="flex items-center gap-1.5">
              <Cpu className="size-3.5 text-primary" /> AI & Traditional Search Optimization
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="size-3.5 text-primary" /> Direct Senior Consultant Access
            </span>
          </div>
        </div>
      </div>
    </section>;
}